"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Zap, Target, Layers, ArrowUpRight } from "lucide-react";

const SUGGESTIONS = [
  {
    icon: Zap,
    title: "Next.js App Router",
    match: "98% Match",
  },
  {
    icon: Target,
    title: "BITV 2.0 & a11y",
    match: "95% Match",
  },
  {
    icon: Layers,
    title: "LCP & Core Web Vitals",
    match: "92% Match",
  },
];

/**
 * 3D-Bühne im Hintergrund + gestochen scharfer Empfehlungs-Stack im Vordergrund.
 */
export default function SmartSuggestionsPreview() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % SUGGESTIONS.length);
    }, 2400);
    return () => clearInterval(timer);
  }, []);

  const current = SUGGESTIONS[index];
  const Icon = current.icon;

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl p-4 select-none"
      style={{ perspective: "900px" }}
    >
      <motion.div
        animate={{ scale: [1, 1.22, 1], opacity: [0.45, 0.85, 0.45] }}
        transition={{ duration: 4.5, repeat: Infinity }}
        className="pointer-events-none absolute left-1/2 top-1/2 h-40 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-purple-500/30 blur-3xl"
      />

      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(168,85,247,0.3)_1px,transparent_1px),linear-gradient(to_bottom,rgba(168,85,247,0.3)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* Gestochen scharfer Vordergrund */}
      <div className="relative z-10 flex flex-col items-center gap-2.5 antialiased">
        <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/60 bg-white px-3 py-1 text-[10px] font-black text-emerald-600 shadow-md dark:bg-zinc-900 dark:text-emerald-400">
          <Sparkles className="h-3 w-3 text-emerald-500" />
          <span>Für dich empfohlen</span>
        </div>

        <div className="relative w-56">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="flex items-center justify-between rounded-2xl border border-emerald-400/80 bg-white p-3 shadow-[0_16px_32px_-6px_rgba(16,185,129,0.5)] dark:bg-zinc-900"
            >
              <div className="flex items-center gap-2.5 overflow-hidden">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-emerald-500 text-white shadow-md">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="overflow-hidden">
                  <p className="truncate font-sans text-xs font-black text-neutral-900 dark:text-white">
                    {current.title}
                  </p>
                  <p className="font-mono text-[10px] font-extrabold text-emerald-600 dark:text-emerald-400">
                    {current.match}
                  </p>
                </div>
              </div>

              <ArrowUpRight className="h-4 w-4 shrink-0 text-emerald-500" />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
